import { FC, SVGProps } from 'react'

import SpotifyIcon from 'src/Assets/Svg/platform-icons/SpotifyLight.svg?react'
import MetaAdsIcon from 'src/Assets/Svg/platform-icons/MetaAds.svg?react'
import TikTokIcon from 'src/Assets/Svg/tiktok_light.svg?react'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'

export type CrossSellPlatform =
  | CampaignPlatformTypeEnum.SPOTIFY
  | CampaignPlatformTypeEnum.TIKTOK
  | CampaignPlatformTypeEnum.META

type IconComponent = FC<SVGProps<SVGSVGElement>>

interface CrossSellCardContent {
  Icon: IconComponent
  chipLabel: string
  title: string
  description: string
  checks: [string, string, string]
  ctaText: string
  footnote: string
  shortName: string
}

export const crossSellCardConfig: Record<
  CrossSellPlatform,
  CrossSellCardContent
> = {
  [CampaignPlatformTypeEnum.SPOTIFY]: {
    Icon: SpotifyIcon,
    chipLabel: 'Best for curator reach',
    title: 'Get your track on real Spotify playlists',
    description:
      "Pitch your music directly to active playlist curators. Professional placements help trigger Spotify's algorithms for long-term discovery.",
    checks: [
      'Direct pitches to genre-matched curators',
      'Guaranteed feedback from industry pros',
      'Trigger Spotify algorithmic playlists',
    ],
    ctaText: 'Launch Spotify Campaign',
    footnote: 'Best for playlisting and organic streams',
    shortName: 'Spotify',
  },
  [CampaignPlatformTypeEnum.META]: {
    Icon: MetaAdsIcon,
    chipLabel: 'Best for targeted traffic',
    title: 'Boost your Spotify growth with Meta Ads',
    description:
      'Reach targeted listeners on Instagram. More streams, saves, and engagement can help support Spotify algorithm growth.',
    checks: [
      'Reach listeners outside playlists',
      'Build more streams and saves',
      'Keep momentum while campaign is active',
    ],
    ctaText: 'Launch Meta Ads campaign',
    footnote: 'Best for streams, saves, and Spotify activity',
    shortName: 'Meta Ads',
  },
  [CampaignPlatformTypeEnum.TIKTOK]: {
    Icon: TikTokIcon,
    chipLabel: 'Best for awareness',
    title: 'Get more creators to use your sound on TikTok',
    description:
      'Drive more awareness around your track through creator videos. More content around your sound can help your release get noticed.',
    checks: [
      'Get creators to post videos with your sound',
      'Create more activity around your release',
      'Increase your chances of viral momentum',
    ],
    ctaText: 'Create TikTok Campaign',
    footnote: 'Best for creator activity and reach',
    shortName: 'TikTok',
  },
}

export const crossSellPairs: Record<
  CrossSellPlatform,
  [CrossSellPlatform, CrossSellPlatform]
> = {
  [CampaignPlatformTypeEnum.SPOTIFY]: [
    CampaignPlatformTypeEnum.META,
    CampaignPlatformTypeEnum.TIKTOK,
  ],
  [CampaignPlatformTypeEnum.TIKTOK]: [
    CampaignPlatformTypeEnum.SPOTIFY,
    CampaignPlatformTypeEnum.META,
  ],
  [CampaignPlatformTypeEnum.META]: [
    CampaignPlatformTypeEnum.SPOTIFY,
    CampaignPlatformTypeEnum.TIKTOK,
  ],
}

export const successIntroMessage: Record<CrossSellPlatform, string> = {
  [CampaignPlatformTypeEnum.SPOTIFY]:
    'Great choice! Now that curators are covered, want to add some social buzz? Launch a TikTok or Meta Ads campaign to reach fans directly and build more momentum.',
  [CampaignPlatformTypeEnum.TIKTOK]:
    'TikTok is ready to go viral! To make sure this buzz turns into long-term growth, consider adding a Spotify or Meta Ads campaign to secure your streams and saves.',
  [CampaignPlatformTypeEnum.META]:
    'Targeted ads are a great start! Want to back them up with organic proof? Pitch to Spotify curators or get TikTok creators involved to amplify your results from day one.',
}

export const BONUS_PLATFORMS: CrossSellPlatform[] = [
  CampaignPlatformTypeEnum.SPOTIFY,
  CampaignPlatformTypeEnum.TIKTOK,
]

export const getBonusCtaText = (
  platform: CrossSellPlatform,
  bonusAmount: number,
): string =>
  `Use $${bonusAmount} Bonus for ${crossSellCardConfig[platform].shortName}`

export const getCrossSellIntroMessage = (
  currentPlatform: CrossSellPlatform,
): string => successIntroMessage[currentPlatform]
